import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import TraderBadge from "../components/TraderBadge";
import BadgeInfoPanel from "../components/BadgeInfoPanel";

function PredictionStats({ userId }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!userId) return;
    api.get("/community/predictions/user/" + userId)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [userId]);

  if (!stats || stats.total === 0) return null;

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
      <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-4">Prediction Accuracy</h2>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="font-display font-bold text-2xl text-yellow-400">{stats.accuracy}%</div>
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Accuracy</div>
        </div>
        <div className="text-center">
          <div className="font-display font-bold text-2xl text-green-400">{stats.correct}</div>
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Correct</div>
        </div>
        <div className="text-center">
          <div className="font-display font-bold text-2xl text-[var(--text-primary)]">{stats.total}</div>
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Total</div>
        </div>
      </div>
      <div className="w-full bg-[var(--bg-elevated)] h-2 rounded-full">
        <div className="h-2 bg-yellow-400 rounded-full transition-all" style={{ width: stats.accuracy + "%" }} />
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile({ name, bio, avatar_url: avatar });
    setSaving(false);
    if (res.ok) toast.success("Profile updated"); else toast.error(res.error);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return toast.error("Please fill in all password fields");
    if (newPassword !== confirmPassword) return toast.error("New passwords do not match");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setChangingPassword(false); }
  };

  return (
    <div className="pt-6 pb-20 max-w-[1400px] mx-auto px-6" data-testid="profile-page">
      <div className="text-center">
        <h1 className="font-display font-bold text-4xl md:text-5xl text-[var(--text-primary)] tracking-tighter">Your Profile</h1>
        <p className="text-[var(--text-secondary)] mt-2 mb-8">Manage your account settings.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-5">
          <BadgeInfoPanel />
        </aside>

        <div className="lg:col-span-6">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 mb-6">
            <div className="flex items-center gap-4 mb-3">
              {avatar ? (
                <img src={avatar} alt={name} className="w-14 h-14 object-cover" onError={(e) => e.target.style.display = "none"} />
              ) : (
                <div className="w-14 h-14 bg-yellow-400 text-black font-bold flex items-center justify-center text-xl">
                  {(user?.name || "U")[0].toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-[var(--text-primary)] font-medium text-sm">{user?.email}</div>
                <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">{user?.role}</div>
              </div>
            </div>
            <TraderBadge userId={user?.id} showInfo />
          </div>
          <PredictionStats userId={user?.id} />
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 mt-6">
            <form onSubmit={save} className="space-y-5">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Display name</label>
                <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Bio</label>
                <textarea rows={4} data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)}
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 resize-none" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Avatar URL (optional)</label>
                <input data-testid="profile-avatar-input" value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="https://..."
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
              </div>
              <button type="submit" disabled={saving} data-testid="profile-save-button"
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 disabled:opacity-50">
                {saving ? "Saving..." : "Save changes"}
              </button>
            </form>
          </div>
        </div>

        <aside className="lg:col-span-3 space-y-5">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
            <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-5">Change Password</h2>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Current Password</label>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full mt-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
              </div>
              <button type="submit" disabled={changingPassword}
                className="w-full bg-[var(--bg-elevated)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[var(--bg-panel)] border border-[var(--border-soft)] disabled:opacity-50 transition">
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}